'use client';
import Image from 'next/image';
import { useState } from 'react';

import { CapitalRequestInput } from '@scaleup/models';
import { useSaveCapitalRequestDraftMutation } from '../../../api';
import { Modal } from '../../../ui';
import { toTitleCase } from '../../../utils';
import { AmountNeeded } from './AmountNeeded';
import { Breakdown } from './Breakdown';
import { CapitalTypes } from './CapitalTypes';
import { Confirmation } from './Confirmation';
import { EBITDA } from './EBITDA';
import { Financials } from './Financials';
import { GrowthPlan } from './GrowthPlan';
import { Introduction } from './Introduction';
import { Ownership } from './Ownership';
import { Requirements } from './Requirements';
import { Review } from './Review';

const sliderSteps = [
  'REQUIREMENTS',
  'INTRODUCTION',
  'CAPITAL TYPES',
  'GROWTH PLAN',
  'FINANCIALS',
  'OWNERSHIP',
  'EBITDA',
  'AMOUNT NEEDED',
  'BREAKDOWN',
  'REVIEW',
  'CONFIRMATION',
];

export const CapitalRequestModal = ({
  showModal,
  setShowModal,
}: {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
}) => {
  const [currentStep, setCurrentStep] = useState<
    | 'REQUIREMENTS'
    | 'INTRODUCTION'
    | 'CAPITAL TYPES'
    | 'GROWTH PLAN'
    | 'FINANCIALS'
    | 'OWNERSHIP'
    | 'EBITDA'
    | 'AMOUNT NEEDED'
    | 'BREAKDOWN'
    | 'REVIEW'
    | 'CONFIRMATION'
  >('REQUIREMENTS');
  const [saveCapitalRequestDraft, { isLoading }] =
    useSaveCapitalRequestDraftMutation();
  const [capitalRequestInput, setCapitalRequestInput] =
    useState<CapitalRequestInput>({
      capitalTypes: [],
      capitalNeed: 0,
      capitalNeedList: [
        {
          amount: 0,
          description: 'build_inventory',
        },
        {
          amount: 0,
          description: 'buy_another_company',
        },
        {
          amount: 0,
          description: 'buy_new_equipment',
        },
        {
          amount: 0,
          description: 'finance_receivables',
        },
        {
          amount: 0,
          description: 'hire_operations_people',
        },
        {
          amount: 0,
          description: 'hire_sales_people',
        },
        {
          amount: 0,
          description: 'purchase_property',
        },
        {
          amount: 0,
          description: 'refinance',
        },
      ],
      SGPProvided: false,
      growthPlans: [],
      financialStatements: [],
      positiveEBITDA: false,
      EBITDARange: {
        min: 0,
        max: 0,
        unknown: false,
        unprovided: false,
      },
      questions: [],
    });

  const handleChangeValue = (
    key: string,
    value:
      | boolean
      | string
      | string[]
      | { fileName: string; s3Key: string }[]
      | { year: number; file: { fileName: string; s3Key: string } }[]
      | {
          min: number;
          max: number;
          unknown: boolean;
          unprovided: boolean;
        }
      | { amount: number; description: string }[]
  ) => {
    setCapitalRequestInput((oldCapitalRequestInput) => ({
      ...oldCapitalRequestInput,
      [key]: value,
    }));
  };

  const handleCloseModal = () => {
    setCurrentStep('REQUIREMENTS');
    setCapitalRequestInput({
      capitalTypes: [],
      capitalNeed: 0,
      capitalNeedList: [],
      SGPProvided: false,
      growthPlans: [],
      financialStatements: [],
      positiveEBITDA: false,
      EBITDARange: {
        min: 0,
        max: 0,
        unknown: false,
        unprovided: false,
      },
      questions: [],
    });
    setShowModal(false);
  };

  const getStepsIcon = (value: number) => {
    const stepNumber = sliderSteps.findIndex((step) => step === currentStep);
    if (stepNumber === value) {
      return 'base';
    }

    if (value > stepNumber) {
      return 'disabled';
    }

    return 'completed';
  };

  return (
    <>
      <Modal
        title={
          currentStep === 'CONFIRMATION'
            ? 'Capital Request Submitted'
            : 'Request Capital'
        }
        icon={
          currentStep === 'CONFIRMATION'
            ? '/icons/sparkles.svg'
            : '/icons/growth.svg'
        }
        confirmation={currentStep === 'CONFIRMATION'}
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        extra={
          currentStep !== 'CONFIRMATION' && (
            <ul className="flex items-center w-full text-xs font-medium sm:text-base border-b border-gray-200 py-5 px-9">
              {sliderSteps.map((title, index) => (
                <li
                  key={index}
                  className={`hover:after:transition-all transition-all ease-out duration-1000 flex items-center justify-center w-full relative after:content-['']  after:w-full after:h-1  ${index >= sliderSteps.findIndex((value) => value === currentStep) ? 'after:bg-gray-200' : 'after:bg-primary-500'}  after:inline-block after:absolute after:top-5 after:left-[50%] [&:last-child]:after:w-0`}
                >
                  <div className="flex items-center	justify-center flex-col gap-[12px] text-xs">
                    <Image
                      src={`/icons/step-icon-${getStepsIcon(index)}.svg`}
                      className="mt-[9px] z-20"
                      alt="step icon"
                      width={24}
                      height={24}
                    />
                    {title !== 'EBITDA' ? toTitleCase(title) : 'EBITDA'}
                  </div>
                </li>
              ))}
            </ul>
          )
        }
      >
        {currentStep === 'REQUIREMENTS' && (
          <Requirements setCurrentStep={setCurrentStep} isLoading={isLoading} />
        )}
        {currentStep === 'INTRODUCTION' && (
          <Introduction setCurrentStep={setCurrentStep} isLoading={isLoading} />
        )}
        {currentStep === 'CAPITAL TYPES' && (
          <CapitalTypes
            capitalRequestInput={capitalRequestInput}
            handleChange={handleChangeValue}
            setCurrentStep={setCurrentStep}
            isLoading={isLoading}
          />
        )}
        {currentStep === 'GROWTH PLAN' && (
          <GrowthPlan
            capitalRequestInput={capitalRequestInput}
            handleChange={handleChangeValue}
            setCurrentStep={setCurrentStep}
            isLoading={isLoading}
          />
        )}
        {currentStep === 'FINANCIALS' && (
          <Financials
            capitalRequestInput={capitalRequestInput}
            handleChange={handleChangeValue}
            setCurrentStep={setCurrentStep}
            isLoading={isLoading}
          />
        )}
        {currentStep === 'OWNERSHIP' && (
          <Ownership setCurrentStep={setCurrentStep} isLoading={isLoading} />
        )}
        {currentStep === 'EBITDA' && (
          <EBITDA
            capitalRequestInput={capitalRequestInput}
            handleChange={handleChangeValue}
            setCurrentStep={setCurrentStep}
            isLoading={isLoading}
          />
        )}
        {currentStep === 'AMOUNT NEEDED' && (
          <AmountNeeded
            capitalRequestInput={capitalRequestInput}
            handleChange={handleChangeValue}
            setCurrentStep={setCurrentStep}
            isLoading={isLoading}
          />
        )}
        {currentStep === 'BREAKDOWN' && (
          <Breakdown
            capitalRequestInput={capitalRequestInput}
            handleChange={handleChangeValue}
            setCurrentStep={setCurrentStep}
            isLoading={isLoading}
          />
        )}
        {currentStep === 'REVIEW' && (
          <Review
            capitalRequestInput={capitalRequestInput}
            setCurrentStep={setCurrentStep}
            isLoading={isLoading}
          />
        )}
        {currentStep === 'CONFIRMATION' && (
          <Confirmation handleCloseModal={handleCloseModal} />
        )}
      </Modal>
    </>
  );
};
